import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FormProvider } from 'react-hook-form';
import { Maximize2, Minimize2 } from 'lucide-react';

const FullScreenModal = ({
  isOpen,
  onOpenChange,
  defaultValues,
  isLoading,
  formMethods,
  handleSubmit,
  handleFormSubmit,
  isSubmitting,
  FormComponent,
  t,
  entityName,
  title,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const modalClassName = isFullscreen
    ? '!p-0 !max-w-none !w-screen !h-screen !rounded-none !border-0'
    : '!p-0 !max-w-4xl !w-[90vw] !max-h-[90vh] !h-[80vh]';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${modalClassName} flex flex-col overflow-hidden transition-all duration-300 ease-in-out`}
        hideClose={true}
      >
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between p-4 border-b bg-muted/30 shrink-0 !me-2">
          <DialogTitle className="text-lg font-semibold">
            {defaultValues?.easy_id 
              ? `${t('common|Update')} : ${t(`entities|${entityName}`) || title} : ${defaultValues.easy_id}`
              : defaultValues?.id 
                ? `${t('common|Update')} : ${t(`entities|${entityName}`) || title}`
                : `${t('common|Create')} : ${t(`entities|${entityName}`) || title}`
            }
          </DialogTitle>

          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-muted !mt-[-10px] !me-[20px]"
            onClick={toggleFullscreen}
            type="button"
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <FormProvider {...formMethods}>
              <div className="h-full flex flex-col">
                <ScrollArea className="flex-1 p-6">
                  <FormComponent />
                </ScrollArea>

                {/* Footer */}
                <div className="border-t bg-muted/20 p-4 shrink-0">
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                    >
                      {t('common|Cancel')}
                    </Button>
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() =>
                        handleSubmit && handleSubmit(handleFormSubmit)()
                      }
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                          {t('common|Saving...')}
                        </>
                      ) : (
                        t('common|Save')
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </FormProvider>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default FullScreenModal;
